import { useNavigate, useParams } from 'react-router-dom';

import { Button, Text } from '@ekhodealer/ekho-common/rev';

import { useDocsAuth } from '@common/hooks/useDocsAuth';
import { useShouldHideSubscribeCta } from '@common/hooks/useShouldHideSubscribeCta';
import { paths } from '@config/paths';
import {
	PRODUCT_MODULES,
	type ProductModuleConfig,
	type ProductModuleId,
	SALES_CALENDAR_URL,
} from '@config/products/productModules';

import './ProductUpgradeOverlay.scss';

/**
 * Extracts a Loom video ID from a share URL and returns the embed URL.
 * Converts `https://www.loom.com/share/{videoId}` to `https://www.loom.com/embed/{videoId}`.
 * Returns null if the URL doesn't match the expected Loom share format.
 */
const toLoomEmbedUrl = (shareUrl: string): string | null => {
	const match = shareUrl.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
	if (!match?.[1]) return null;
	return `https://www.loom.com/embed/${match[1]}`;
};

interface ProductUpgradeOverlayProps {
	moduleId: ProductModuleId;
}

/**
 * Frosted overlay shown over a product page when the dealer
 * hasn't subscribed to that module. Renders stacked CTAs:
 *   1. Contact Sales — opens HubSpot calendar in a new tab
 *   2. Subscribe — navigates to onboarding flow with the product pre-selected
 *   3. Learn more — links to product docs (if docsPath is configured)
 *
 * When a `promoVideoUrl` is configured, a responsive Loom embed
 * is rendered above the title.
 *
 * Reusable across any product page.
 */
export const ProductUpgradeOverlay = ({ moduleId }: ProductUpgradeOverlayProps): React.JSX.Element => {
	const module: ProductModuleConfig = PRODUCT_MODULES[moduleId];
	const navigate = useNavigate();
	const { servicesProviderId } = useParams();
	const { openDocs, prefetchToken } = useDocsAuth();
	const { shouldHide: hideSubscribeCta } = useShouldHideSubscribeCta();

	const embedUrl = module.promoVideoUrl ? toLoomEmbedUrl(module.promoVideoUrl) : null;

	const handleContactSales = (): void => {
		window.open(SALES_CALENDAR_URL, '_blank', 'noopener,noreferrer');
	};

	const handleSubscribe = (): void => {
		if (!module.onboardingProductId || !servicesProviderId) return;

		// Navigate to the existing dealer subscribe step with the product pre-selected.
		// The svpId in the URL identifies the dealer and encodes that they are an existing dealer.
		navigate(
			`${paths.svpSubscribe.getHref(servicesProviderId)}?product=${module.onboardingProductId}`,
		);
	};

	const handleLearnMore = (): void => {
		if (module.docsPath) {
			openDocs(module.docsPath);
		}
	};

	return (
		<div data-component='product-upgrade-overlay'>
			<div data-element='card'>
				{embedUrl && (
					<div data-element='video-wrapper'>
						{/* Third-party Loom embed — raw iframe is acceptable per frontend rules */}
						<iframe
							src={embedUrl}
							allow='fullscreen; clipboard-write; encrypted-media; picture-in-picture'
							title={`${module.displayName} promo video`}
						/>
					</div>
				)}
				<Text size='title200' weight='bold'>
					Unlock {module.displayName}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{module.description}
				</Text>
				<div data-element='actions'>
					<Button variant='filled' size='medium' isFullWidth onPress={handleContactSales}>
						Contact sales
					</Button>
					{!hideSubscribeCta && (
						<Button variant='secondary' size='medium' isFullWidth onPress={handleSubscribe}>
							{module.subscribeCta}
						</Button>
					)}
					{module.docsPath && (
						<div onMouseEnter={prefetchToken}>
							<Button
								variant='ghost'
								size='medium'
								isFullWidth
								onPress={handleLearnMore}>
								Learn more
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
